import React, { useEffect, useState } from "react";
import axios from "axios";
import { Building2, Receipt, ShieldAlert } from "lucide-react";
import { BUSINESS_STATUS_LABELS } from "../../components/b2b/B2BBadges";
import { BankDetailsBlock } from "../../components/b2b/BankDetailsBlock";
import { ContactDetailsBlock } from "../../components/profile/ContactDetailsBlock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BusinessStatusSettings = ({ user, showToast, onSaved }) => {
  const [current, setCurrent] = useState(null);
  const [status, setStatus] = useState("none");
  const [inn, setInn] = useState("");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/b2b/profile/business-status`).then(r => {
    setCurrent(r.data);
    setStatus(r.data.business_status || "none");
    setConsent(!!r.data.pd_transfer_consent);
  }).catch(() => {});

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/b2b/profile/business-status`, { business_status: status, inn: inn || undefined, pd_transfer_consent: consent });
      showToast(status === "none" ? "Бизнес-статус снят" : `Статус «${r.data.label}» сохранён`, "success");
      setInn("");
      await load();
      if (onSaved) onSaved();
      localStorage.setItem("rv_profile_changed", String(Date.now()));
    } catch (e) {
      showToast(e.response?.data?.detail || "Не удалось сохранить статус", "error");
    } finally {
      setSaving(false);
    }
  };

  const locked = current?.locked_until && new Date(current.locked_until) > new Date();
  const needInn = status !== "none";
  // Ж10: разрядность ИНН для выбранного статуса и применимость текущего ИНН
  const expectedLen = status === "ooo" ? 10 : 12;
  const currentFits = !!current?.inn_masked && current?.inn_len === expectedLen;
  const innRequired = needInn && !currentFits;
  const innHint = !needInn ? "" : currentFits
    ? `Оставьте пустым, чтобы сохранить текущий ИНН (${current.inn_masked}), или введите новый из ${expectedLen} цифр.`
    : current?.inn_masked
      ? `Обязательно: введите новый ИНН из ${expectedLen} цифр — текущий ИНН (${current.inn_len || "другой"} цифр) не подходит для статуса «${BUSINESS_STATUS_LABELS[status]}».`
      : `Обязательно: ИНН из ${expectedLen} цифр.`;
  const innLenError = needInn && inn && inn.length !== expectedLen ? `Для статуса «${BUSINESS_STATUS_LABELS[status]}» нужно ${expectedLen} цифр, введено ${inn.length}.` : "";

  return (
    <div className="max-w-2xl mx-auto" data-testid="business-status-settings">
      <h2 className="text-2xl font-bold mb-2 flex items-center gap-2" style={{ fontFamily: "Manrope,sans-serif", color: "var(--rv-navy)" }}>
        <Building2 size={22} style={{ color: "var(--rv-teal)" }} /> Бизнес-статус
      </h2>
      <p className="text-sm mb-5" style={{ color: "var(--rv-text-secondary)" }}>
        Задания от организаций (с оплатой напрямую и закрывающими документами) доступны только исполнителям со статусом самозанятого, ИП или ООО.
        Организация платит вам напрямую по вашим реквизитам, а вы выдаёте ей чек.
      </p>

      {current && current.business_status !== "none" && (
        <div data-testid="business-status-current" className="mb-4 p-3 rounded-lg flex items-center gap-2 text-sm" style={{ background: "#ECFDF5", border: "1px solid #6EE7B7", color: "#065F46" }}>
          <Receipt size={16} /> Текущий статус: <strong>{current.label}</strong>{current.inn_masked ? `, ИНН ${current.inn_masked}` : ""}
        </div>
      )}
      {current?.warnings > 0 && (
        <div data-testid="business-status-warnings" className="mb-4 p-3 rounded-lg flex items-start gap-2 text-sm" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}>
          <ShieldAlert size={16} className="mt-0.5" />
          <span>Подтверждённых жалоб на невыдачу чека: {current.warnings}. {locked ? `Установка статуса недоступна до ${new Date(current.locked_until).toLocaleDateString("ru-RU")}.` : "При повторной жалобе статус будет снят на 30 дней."}</span>
        </div>
      )}

      <div className="bg-white rounded-xl p-5 space-y-4" style={{ border: "1px solid var(--rv-border)" }}>
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--rv-text-primary)" }}>Статус</label>
          <select data-testid="business-status-select" value={status} onChange={e => setStatus(e.target.value)} disabled={locked}
            className="w-full px-3 py-2 rounded-lg text-sm" style={{ border: "1px solid var(--rv-border)" }}>
            {Object.entries(BUSINESS_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {needInn && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--rv-text-primary)" }}>
                ИНН {status === "ooo" ? "(10 цифр)" : "(12 цифр)"}{current?.inn_masked ? ` — сейчас ${current.inn_masked}` : ""}
              </label>
              <input data-testid="business-inn-input" value={inn} onChange={e => setInn(e.target.value.replace(/\D/g, "").slice(0, 12))}
                placeholder={currentFits ? "Оставьте пустым, чтобы не менять" : `Введите ИНН (${expectedLen} цифр)`} inputMode="numeric"
                className="w-full px-3 py-2 rounded-lg text-sm" style={{ border: `1px solid ${innLenError || (innRequired && !inn) ? "#FCA5A5" : "var(--rv-border)"}` }} />
              <p data-testid="business-inn-hint" className="text-xs mt-1 font-medium" style={{ color: innRequired ? "#B45309" : "var(--rv-text-secondary)" }}>{innHint}</p>
              {innLenError && <p data-testid="business-inn-error" className="text-xs mt-1" style={{ color: "var(--rv-error)" }}>{innLenError}</p>}
              <p className="text-xs mt-1" style={{ color: "var(--rv-text-muted)" }}>ИНН хранится в зашифрованном виде и показывается заказчику только в акте выполненных работ.</p>
            </div>
            <label className="flex items-start gap-2 text-sm" style={{ color: "var(--rv-text-secondary)" }}>
              <input type="checkbox" data-testid="pd-transfer-consent-checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5" style={{ accentColor: "var(--rv-teal)" }} />
              <span>Даю согласие на передачу моих ФИО и ИНН заказчикам заданий от организаций для оформления договора и акта (152-ФЗ).</span>
            </label>
            <div className="text-xs p-3 rounded-lg" style={{ background: "#F8FAFC", border: "1px solid var(--rv-border)", color: "var(--rv-text-secondary)" }}>
              Статус указывается добровольно и не проверяется платформой. Ответственность за достоверность статуса и выдачу чека заказчику несёт исполнитель.
              При подтверждённой жалобе на невыдачу чека: предупреждение, затем снятие статуса на 30 дней, затем блокировка аккаунта.
            </div>
          </>
        )}
        <button data-testid="business-status-save-btn" onClick={save} disabled={saving || locked || (needInn && !consent) || (innRequired && !inn) || !!innLenError}
          className="reversia-btn reversia-btn-teal reversia-btn-md">
          {saving ? "Сохранение..." : "Сохранить"}
        </button>
      </div>
      {user?.role === "executor" && <div className="mt-4"><ContactDetailsBlock showToast={showToast} onSaved={onSaved} /></div>}
      {user?.role === "executor" && <BankDetailsBlock showToast={showToast} />}
    </div>
  );
};
